import {
  PortalDTO,
  PortalListDTO,
  NumberDTO,
  PropertyClient,
  InviteCodeDTO,
  SiteRoleDTO,
  DocumentDTO,
  LinkSectionDTO,
  SaveLink,
  SaveLinkSection,
  LinkClient,
  FileParameter,
  SwaggerException,
  SaveLinkSectionOrderRequest,
  SaveDocumentOrderRequest,
  SaveNumberOrderRequest,
  BulkTenantRequest,
  SaveSiteRoleOrderRequest,
  DeleteNumberCommand,
  ImportLinksFromPortalRequest,
  SaveTenantCommand,
  SaveContentRequest,
  PortalSearchDTO,
  SaveTenantOrderRequest,
  SaveUserCollectionRequest,
  DeleteUserCollectionRequest,
  PermissionType,
} from "./generatedFetchClient";
import { defineStore, acceptHMRUpdate } from "pinia";
import { TrackJS } from "trackjs";
import { cacheDocuments } from "../helpers";
import { useMetrics } from "../helpers/metrics";

let propertyClient = new PropertyClient();
let linkClient = new LinkClient();
let metrics = useMetrics();

export const useBldgStore = defineStore({
  id: "bldgStore",
  state() {
    return {
      bldgCode: "",
      building: null as PortalDTO | null,
      isLoading: false,
      isPortalListLoading: false,
      properties: [] as PortalSearchDTO[],
      portalList: [] as PortalListDTO[],
      inviteCodes: [] as InviteCodeDTO[],
      linkSections: [] as LinkSectionDTO[],
      maxOfflineDocSize: 20_000_000,
      serverVersion: "",
    };
  },
  actions: {
    getStartupData() {
      this.getPropertyList();
    },
    setAuthToken(token: string) {
      linkClient.setAuthToken(token);
      propertyClient.setAuthToken(token);
      //console.log("BldgStore Token set");
    },
    lookupSiteRoleName(siteRoleId: number | undefined): SiteRoleDTO | null {
      if (this.building?.siteRoles?.length && this.building.siteRoles.length > 0) {
        return this.building.siteRoles.find((s) => s.siteRoleId == siteRoleId) ?? null;
      }
      return null;
    },
    // Get a flat list of clients / properties for admin lookups
    async getPropertyList(force: boolean = false) {
      if (!force && ((this.properties && this.properties.length > 0) || this.isLoading)) {
        //console.log("Ignore extra call to getPropertyList() ");
        return;
      }

      this.isPortalListLoading = true;
      console.log("bldgStore > getPropertyList() called");

      try {
        let portalSearchVm = await propertyClient.getPropertySearch();
        this.properties = portalSearchVm.portals ?? [];
      } catch (apiError: any) {
        console.error("error getting portal list", apiError);
      } finally {
        this.isPortalListLoading = false;
      }
    },
    // Get simple list of portals for a given cliendID.
    // Used by SsoPortalList.vue
    async getPortalList(clientSystemName: string, regionName?: string) {
      console.log("bldgStore > getPortalList() called");
      try {
        this.portalList = await propertyClient.getPortalsForClient(
          clientSystemName,
          regionName,
          undefined,
        );
      } catch (apiError: any) {
        console.error("error getting portal list", apiError);
      }
    },
    async getPortalsWithPermission(
      clientSystemName: string,
      regionName?: string,
      permissionFilter?: PermissionType,
    ): Promise<PortalListDTO[]> {
      console.log("bldgStore > getPortalsWithPermission() called");
      try {
        var list = await propertyClient.getPortalsForClient(
          clientSystemName,
          regionName,
          permissionFilter,
        );
        return list;
      } catch (apiError: any) {
        console.error("error getting portal list", apiError);
      }
    },
    async tryGetBuildingData(bldgCode: string, force: boolean = false) {
      if (this.property !== null && this.bldgCode === bldgCode && !force) {
        //Building already loaded
        return;
      }

      if (!bldgCode || bldgCode.length < 2) {
        //Ignoring API call request when site code not set
        return;
      }

      try {
        this.isLoading = true;
        this.bldgCode = bldgCode;
        //console.log(`Loading building data for ${bldgCode}...`);

        this.building = await propertyClient.getPortalByCode(bldgCode);
        this.CacheDocuments();

        console.log("Loaded building data", this.building);
        console.log("Server version is at " + this.building.serverVersion);
        this.serverVersion = this.building.serverVersion ?? "";

        if ((import.meta as any).env.PROD) {
          TrackJS.addMetadata("server_version", this.building.serverVersion!);
        }

        // Sort building tenants
        this.building.tenants?.sort((x, y) => {
          if ((x.sortOrder ?? 0) < (y.sortOrder ?? 0)) {
            return -1;
          } else if ((x.sortOrder ?? 0) > (y.sortOrder ?? 0)) {
            return 1;
          }

          //If sortOrders are equal, sort by name
          return (x.name ?? "").localeCompare(y.name ?? "");
        });

        // Sort building documents
        this.building.documents?.sort((x, y) => {
          if ((x.sortOrder ?? 0) < (y.sortOrder ?? 0)) {
            return -1;
          } else if ((x.sortOrder ?? 0) > (y.sortOrder ?? 0)) {
            return 1;
          } else {
            //If sortOrders are equal, sort by id
            if ((x.documentId ?? 0) < (y.documentId ?? 0)) {
              return -1;
            } else if ((x.documentId ?? 0) > (y.documentId ?? 0)) {
              return 1;
            }
          }
          return 0;
        });

        // Sort building contacts
        this.building.numbers?.sort((x, y) => {
          if ((x.sortOrder ?? 0) < (y.sortOrder ?? 0)) {
            return -1;
          } else if ((x.sortOrder ?? 0) > (y.sortOrder ?? 0)) {
            return 1;
          } else {
            //If sortOrders are equal, sort by id
            if ((x.numberId ?? 0) < (y.numberId ?? 0)) {
              return -1;
            } else if ((x.numberId ?? 0) > (y.numberId ?? 0)) {
              return 1;
            }
          }
          return 0;
        });

        // Sort building roles
        this.building.siteRoles?.sort((x, y) => {
          if ((x.sortOrder ?? 0) < (y.sortOrder ?? 0)) {
            return -1;
          } else if ((x.sortOrder ?? 0) > (y.sortOrder ?? 0)) {
            return 1;
          } else {
            //If sortOrders are equal, sort by id
            if ((x.siteRoleId ?? 0) < (y.siteRoleId ?? 0)) {
              return -1;
            } else if ((x.siteRoleId ?? 0) > (y.siteRoleId ?? 0)) {
              return 1;
            }
          }
          return 0;
        });

        //Set group identities in metrics
        metrics.setGroups(this.building);

        this.isLoading = false;
      } catch (ex: any) {
        let apiError = ex as SwaggerException;
        if (apiError && apiError.status === 404) {
          console.warn(apiError?.response ?? "Portal not found");
        } else {
          console.warn(`Error getting building data for ${bldgCode}`, ex);
        }

        this.isLoading = false;
      }
    },
    async savePropertyNumber(portalId: number, saveNum: NumberDTO): Promise<string> {
      try {
        let property = this.property;
        if (property === null || property.portalId !== portalId) {
          let errorStr = "Number can only be saved to a selected property";
          console.error(errorStr);
          return errorStr;
        }
        saveNum.portalId = portalId;

        await propertyClient.saveNumber(saveNum);
        if (property.siteCode) this.tryGetBuildingData(property.siteCode, true);
        return "";
      } catch (apiError: any) {
        console.error("Error saving number", apiError);
        return apiError.response;
      }
    },
    async deletePropertyNumber(numberId: number): Promise<boolean> {
      try {
        let response = await propertyClient.deleteNumber({
          numberId: numberId,
          portalId: this.building?.portalId ?? 0,
        } as DeleteNumberCommand);
        if (response && response.status === 200) {
          this.tryGetBuildingData(this.bldgCode, true);
          return true;
        }
      } catch (error) {
        console.error("Error deleting number", error);
        return false;
      }
      return false;
    },
    async saveNumberOrder(newNumberOrder: SaveNumberOrderRequest) {
      newNumberOrder.portalId = this.building?.portalId ?? 0;
      try {
        await propertyClient.saveNumberOrder(newNumberOrder);

        this.tryGetBuildingData(this.bldgCode, true);
      } catch (apiError: any) {
        console.error("Error saving numbers order", apiError);
        return apiError.response;
      }
    },
    async uploadDoc(
      file: FileParameter,
      siteCode: string,
      oldFilename: string = "",
    ): Promise<string> {
      try {
        let response = await propertyClient.uploadDocumentFile(
          siteCode,
          this.building?.portalId ?? 0,
          oldFilename,
          file,
        );

        if (response) {
          console.log("Upload of document successful");
          return "";
        } else {
          let error = "problem uploading document";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error uploading Document", apiError);
        return apiError.response;
      }
    },
    async renameDoc(siteCode: string, oldFilename: string, newFilename: string): Promise<string> {
      try {
        let response = await propertyClient.renameDocumentFile(
          siteCode,
          oldFilename,
          newFilename,
          this.building?.portalId ?? 0,
        );

        if (response) {
          console.log("Rename of document successful");
          return "";
        } else {
          let error = "problem renaming document";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error renaming Document", apiError);
        return apiError.response;
      }
    },
    async saveDoc(doc: DocumentDTO): Promise<string> {
      try {
        if (this.property === null || this.property.portalId !== doc.portalId) {
          let error = "Document can only be saved to a selected property";
          console.error(error);
          return error;
        }

        let response = await propertyClient.saveDocument(doc);

        if (response) {
          console.log("Save of document successful");
          return "";
        } else {
          let error = "problem saving document";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error saving Document", apiError);
        return apiError.response;
      }
    },
    async deleteDoc(id: number): Promise<boolean> {
      try {
        let response = await propertyClient.deleteDocument(this.building?.portalId ?? 0, id);
        if (response && response.status === 200) {
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error deleting doc", error);
        return false;
      }
      return false;
    },
    async saveDocOrder(newOrder: SaveDocumentOrderRequest) {
      try {
        newOrder.portalId = this.building?.portalId ?? 0;
        await propertyClient.saveDocOrder(newOrder);

        this.tryGetBuildingData(this.bldgCode, true);
      } catch (apiError: any) {
        console.error("Error saving doc order", apiError);
        return apiError.response;
      }
    },
    async saveTenant(tenant: SaveTenantCommand): Promise<string> {
      try {
        let property = this.property;
        if (property === null || property.propertyId !== tenant.propertyId) {
          let error = "Tenant can only be saved to a selected property";
          console.error(error);
          return error;
        }

        await propertyClient.saveTenant(tenant);

        console.log("Save of tenant successful");
        await this.tryGetBuildingData(this.bldgCode, true);
        return "";
      } catch (apiError: any) {
        console.error("Error saving Tenant", apiError);
        return apiError.response;
      }
    },
    async saveTenantOrder(newOrder: SaveTenantOrderRequest) {
      try {
        newOrder.portalId = this.building?.portalId ?? 0;
        await propertyClient.saveTenantOrder(newOrder);

        this.tryGetBuildingData(this.bldgCode, true);
      } catch (apiError: any) {
        console.error("Error saving tenant order", apiError);
        return apiError.response;
      }
    },
    async bulkSaveTenant(bulkTenantDTO: BulkTenantRequest): Promise<string> {
      try {
        await propertyClient.bulkSaveTenant(bulkTenantDTO);
        return "";
      } catch (apiError: any) {
        console.error("Error bulk saving tenants", apiError);
        return apiError.response;
      }
    },
    async getTenantUserCount(tenantId: number): Promise<number> {
      try {
        let response = await propertyClient.getTenantUserCount(
          tenantId,
          this.property?.portalId ?? 0,
        );
        return response;
      } catch (apiError: any) {
        console.error("Error getting tenant user count", apiError);
        return -1;
      }
    },
    async disableTenantUsers(tenantId: number): Promise<string> {
      try {
        await propertyClient.disableTenantUsers({
          tenantId: tenantId,
          portalId: this.property?.portalId ?? 0,
        });

        this.tryGetBuildingData(this.bldgCode, true);
      } catch (error) {
        let msg = "Error disabling tenant users: " + error;
        console.error(msg);
        return msg;
      }
      return "";
    },
    async deleteTenant(tenantId: number): Promise<string> {
      try {
        let response = await propertyClient.deleteTenant(tenantId, this.property?.portalId ?? 0);
        if (response && response.status === 200) {
          this.tryGetBuildingData(this.bldgCode, true);
          return "";
        }
        let msg = "Problem deleting tenant: " + response?.status;
        console.warn(msg);
        return msg;
      } catch (error) {
        let msg = "Error deleting tenant: " + error;
        console.error(msg);
        return msg;
      }
    },
    async lookupInviteCode(portalId: number, code: string): Promise<InviteCodeDTO | string> {
      try {
        console.log(`Looking up invite code ${code} for portal ${portalId} `);
        let inviteCodesVm = await propertyClient.lookupInviteCode(portalId, code);
        if (inviteCodesVm && inviteCodesVm.inviteCodes && inviteCodesVm.inviteCodes.length > 0) {
          return inviteCodesVm.inviteCodes[0];
        }
      } catch (apiError: any) {
        console.error(`Error looking up code ${code}`, apiError);
        if (apiError.response?.status === 404) {
          return "This invite code was not found";
        }
        return apiError.message;
      }
      return "Invite Code not Found";
    },
    async getAutoActivateInviteCodes(portalId: number): Promise<InviteCodeDTO[] | string> {
      try {
        console.log(`Looking for default for portal ${portalId} `);
        let inviteCodesVm = await propertyClient.getAutoActivateInviteCodes(portalId);
        if (inviteCodesVm && inviteCodesVm.inviteCodes && inviteCodesVm.inviteCodes.length > 0) {
          return inviteCodesVm.inviteCodes;
        }
      } catch (apiError: any) {
        console.error(`Error looking up default codes`, apiError);
        if (apiError.response?.status === 404) {
          return "Default invite codes were not found";
        }
        return apiError.message;
      }
      return "Invite Codes not Found";
    },
    async getInviteCodes(portalId: number): Promise<boolean> {
      try {
        let vm = await propertyClient.getInviteCodes(portalId);
        if (vm && vm.inviteCodes) {
          this.inviteCodes = vm.inviteCodes;
        }
        return false;
      } catch (error) {
        console.error(`error getting invite codes for Portal# ${portalId}`, error);
        return false;
      }
    },
    async saveInviteCode(portalId: number, saveCode: InviteCodeDTO): Promise<string | null> {
      try {
        let property = this.property;
        if (property === null || property.portalId !== portalId) {
          console.warn("Invite Code can only be saved to a selected property");
          return "Invite Code can only be saved to a selected property";
        }

        await propertyClient.saveInviteCode(saveCode);
        this.getInviteCodes(property.portalId);

        return null;
      } catch (apiError: any) {
        console.error("error saving invite code", apiError);
        return apiError.message;
      }
    },
    async deleteInviteCode(numberId: number): Promise<string> {
      try {
        //let response = await http.get(`/api/property/deleteInviteCode/${numberId}`);
        await propertyClient.deleteInviteCode(numberId);
        if (this.property && this.property.portalId) {
          await this.getInviteCodes(this.property.portalId);
        }
        return "";
      } catch (error) {
        let msg = "Error deleting invite code: " + error;
        console.error(msg);
        return msg;
      }
    },
    async saveSiteRole(portalId: number, saveRole: SiteRoleDTO): Promise<string> {
      try {
        let property = this.property;
        if (property === null || property.portalId !== portalId) {
          let error = "Role can only be saved to a selected property";
          console.warn(error);
          return error;
        }

        saveRole.portalId = portalId;
        await propertyClient.saveRole(saveRole);
        this.tryGetBuildingData(this.bldgCode, true);
        return "";
      } catch (apiError: any) {
        console.error("error saving role", apiError);
        return apiError.response;
      }
    },
    async deleteSiteRole(siteRoleId: number): Promise<string> {
      try {
        await propertyClient.deleteRole(siteRoleId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting siterole", apiError);
        return apiError.response;
      }
    },
    async saveRoleOrder(newOrder: SaveSiteRoleOrderRequest) {
      try {
        await propertyClient.saveRoleOrder(newOrder);

        this.tryGetBuildingData(this.bldgCode, true);
      } catch (apiError: any) {
        console.error("Error saving role order", apiError);
        return apiError.response;
      }
    },
    async getLinks(portalId: number): Promise<boolean> {
      try {
        this.linkSections = await linkClient.getLinks(portalId);

        this.linkSections.sort((x, y) => {
          const xSortOrder = x.linkSectionSortOrder ?? 0;
          const ySortOrder = y.linkSectionSortOrder ?? 0;
          if (xSortOrder < ySortOrder) {
            return -1;
          } else if (xSortOrder > ySortOrder) {
            return 1;
          } else {
            //If sortOrders are equal, sort by id
            const xId = x.id ?? 0;
            const yId = y.id ?? 0;
            if (xId < yId) {
              return -1;
            } else if (xId > yId) {
              return 1;
            }
          }
          return 0;
        });

        this.linkSections.forEach((linkSection) => {
          linkSection.links?.sort((x, y) => {
            const xSortOrder = x.linkSortOrder ?? 0;
            const ySortOrder = y.linkSortOrder ?? 0;
            if (xSortOrder < ySortOrder) {
              return -1;
            } else if (xSortOrder > ySortOrder) {
              return 1;
            } else {
              //If sortOrders are equal, sort by id
              const xLinkId = x.linkId ?? 0;
              const yLinkId = y.linkId ?? 0;
              if (xLinkId < yLinkId) {
                return -1;
              } else if (xLinkId > yLinkId) {
                return 1;
              }
            }
            return 0;
          });
        });

        return true;
      } catch (apiError: any) {
        console.error("error getting links", apiError);
        return false;
      }
    },
    async saveLink(link: SaveLink): Promise<string> {
      try {
        console.log("saving link", link);
        let pk = await linkClient.saveLink(link);
        if (pk > 0) {
          if (this.building?.portalId) {
            await this.getLinks(this.building?.portalId);
          }
          return "";
        }
        return "No link record was updated";
      } catch (apiError: any) {
        console.error("error saving link", apiError);
        return apiError?.response;
      }
    },
    async saveLinkSection(section: SaveLinkSection): Promise<string> {
      try {
        console.log("saving link section", section);
        let pk = await linkClient.saveLinkSection(section);
        if (pk > 0) {
          if (this.building?.portalId) {
            await this.getLinks(this.building?.portalId);
          }
          return "";
        }
        return "No link section record was updated";
      } catch (apiError: any) {
        console.error("error saving link section", apiError);
        return apiError?.response?.data?.toString();
      }
    },
    async deleteLink(linkId: number): Promise<string> {
      try {
        await linkClient.deleteLink(linkId);
        if (this.building?.portalId) {
          await this.getLinks(this.building?.portalId);
        }
        return "";
      } catch (apiError: any) {
        console.error("Error deleting Link", apiError);
        return apiError.response;
      }
    },
    async deleteLinkSection(id: number): Promise<string> {
      try {
        await linkClient.deleteLinkSection(id);
        if (this.building?.portalId) {
          await this.getLinks(this.building?.portalId);
        }
        return "";
      } catch (apiError: any) {
        console.error("Error deleting Link Section", apiError);
        return apiError.response;
      }
    },
    async saveLinkOrder(newOrder: SaveLinkSectionOrderRequest) {
      try {
        await linkClient.saveLinkOrder(newOrder);
        if (this.building?.portalId) {
          await this.getLinks(this.building?.portalId);
        }
      } catch (apiError: any) {
        console.error("Error saving link order", apiError);
        return apiError.response;
      }
    },
    async importLinksFromPortal(request: ImportLinksFromPortalRequest) {
      try {
        await linkClient.importLinksFromPortal(request);
        if (this.building?.portalId) {
          await this.getLinks(this.building?.portalId);
        }
      } catch (apiError: any) {
        console.error("Error importing links from portal", apiError);
        return apiError.response;
      }
    },
    async uploadContent(
      file: FileParameter,
      siteCode: string,
      oldFilename: string = "",
    ): Promise<string> {
      try {
        let response = await propertyClient.uploadContentFile(siteCode, oldFilename, file);

        if (response) {
          console.log("Upload of content successful");
          return "";
        } else {
          let error = "Problem uploading content";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error uploading content", apiError);
        return apiError.response;
      }
    },
    async renameUploadedContent(
      siteCode: string,
      oldFilename: string,
      newFilename: string,
    ): Promise<string> {
      try {
        let response = await propertyClient.renameContentFile(siteCode, oldFilename, newFilename);

        if (response) {
          console.log("Rename of content successful");
          return "";
        } else {
          let error = "problem renaming content";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error renaming content", apiError);
        return apiError.response;
      }
    },
    async saveContent(request: SaveContentRequest): Promise<string> {
      try {
        if (this.property === null || this.property.portalId !== request.portalId) {
          let error = "content can only be saved to a selected property";
          console.error(error);
          return error;
        }

        let response = await propertyClient.saveContent(request);

        if (response) {
          console.log("Save of content successful");
          return "";
        } else {
          let error = "problem saving content";
          console.warn(error);
          return error;
        }
      } catch (apiError: any) {
        console.error("Error saving content", apiError);
        return apiError.response;
      }
    },
    async deleteContent(contentId: number, siteCode: string = ""): Promise<string> {
      try {
        siteCode = siteCode || (this.property?.siteCode ?? "");
        await propertyClient.deleteContent(contentId, siteCode);
        return "";
      } catch (error: any) {
        console.error("Error deleting content", error);
        return error.response;
      }
    },
    async saveUserCollection(saveUserCollection: SaveUserCollectionRequest): Promise<string> {
      try {
        let property = this.property;
        if (property === null || property.portalId !== saveUserCollection.portalId) {
          let error = "User collection can only be saved to a selected property";
          console.warn(error);
          return error;
        }

        await propertyClient.saveUserCollection(saveUserCollection);
        await this.tryGetBuildingData(this.bldgCode, true);
        return "";
      } catch (apiError: any) {
        console.error("error saving user collection", apiError);
        return apiError.response;
      }
    },
    async deleteUserCollection(deleteUserCollection: DeleteUserCollectionRequest): Promise<string> {
      try {
        let property = this.property;
        if (property === null || property.portalId !== deleteUserCollection.portalId) {
          let error = "User collection can only be deleted from a selected property";
          console.warn(error);
          return error;
        }

        await propertyClient.deleteUserCollection(deleteUserCollection);
        await this.tryGetBuildingData(this.bldgCode, true);
        return "";
      } catch (apiError: any) {
        console.error("error deleting user collection", apiError);
        return apiError.response;
      }
    },
    async CacheDocuments() {
      if (
        (import.meta as any).env.PROD &&
        (import.meta as any).env.VITE_ENABLE_OFFLINE_CACHEING.toLowerCase() == "true" &&
        !window.location.pathname.includes("/staff/")
      ) {
        let docsToCache =
          this.building?.documents?.filter(
            (doc) => (doc.fileSize ?? 0) <= this.maxOfflineDocSize && doc.isAvailableOffline,
          ) ?? [];
        cacheDocuments(docsToCache, this.siteCode);
      }
    },
    async getServerVersion(): Promise<string> {
      try {
        this.serverVersion = await propertyClient.version();
        return this.serverVersion;
      } catch (apiError: any) {
        console.error("error getting server version");
        return "";
      }
    },
  },
  getters: {
    property(): PortalDTO | null {
      let property = this.building as PortalDTO;
      if (property && property.portalId !== undefined && property.portalId > 0) {
        return property;
      }
      return null;
    },
    siteCode(): string {
      return this.building?.siteCode ?? "";
    },
  },
});

//Enable Hot Module Replacement
// https://pinia.esm.dev/cookbook/hot-module-replacement.html
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useBldgStore as any, (import.meta as any).hot));
}
